import { Box, Button, Icon, Text, Typography } from "components/ui";
import { Calendar } from "lucide-react-native";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Platform } from "react-native";
import DateTimePickerModal from "react-native-modal-datetime-picker";
import { formatDispatchDate } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

type Props = {
	isDateRange?: boolean;
	onDateChange?: (dates: any) => void;
	minWidth?: number;
	isLight?: boolean;
	initialStartDate?: Date | null;
	initialEndDate?: Date | null;
};

const DateSelect = ({ isDateRange, onDateChange, minWidth, isLight, initialStartDate, initialEndDate }: Props) => {
	const [date, setDate] = useState(new Date());
	const [open, setOpen] = useState(false);

	const [startDate, setStartDate] = useState(initialStartDate);
	const [endDate, setEndDate] = useState(initialEndDate);

	const [isDatePickerVisible, setDatePickerVisibility] = useState(false);

	const showDatePicker = () => {
		setDatePickerVisibility(true);
	};

	const hideDatePicker = () => {
		setDatePickerVisibility(false);
	};

	const handleConfirm = (date: any) => {
		console.warn("A date has been picked: ", date);
		setDate(date);
		onDateChange && onDateChange(date);
		hideDatePicker();
	};

	const handleDateRangeConfirm = (dates: any) => {
		const [start, end] = dates;
		setStartDate(start);
		setEndDate(end);
		if (!start && !end) {
			onDateChange && onDateChange({ startDate: null, endDate: null });
		} else if (end) {
			onDateChange && onDateChange({ startDate: start, endDate: end });
		}
	};

	const handleClear = () => {
		setStartDate(null);
		setEndDate(null);
		setDate(new Date());
		onDateChange && onDateChange({ startDate: null, endDate: null });
	};

	const isMobile = useIsMobile();

	if (Platform.OS !== "web") {
		return (
			<Box>
				<Button borderRadius={isMobile ? 8 : 12} onPress={showDatePicker} gap={12} padding={isMobile ? 10 : 12} borderWidth={1} justifyContent="flex-start">
					<Icon color={"$basicblack"} as={Calendar} />
					<Typography sx={{ fontSize: isMobile ? 14 : 16 }} weight={isMobile ? "normal" : "bold"}>
						{formatDispatchDate(date)}
					</Typography>
				</Button>

				{isMobile && <DateTimePickerModal isVisible={isDatePickerVisible} mode="date" onConfirm={handleConfirm} onCancel={hideDatePicker} />}
			</Box>
		);
	}

	return (
		<Box>
			{isDateRange ? (
				<DatePicker
					selected={startDate}
					onChange={handleDateRangeConfirm}
					startDate={startDate}
					endDate={endDate}
					selectsRange
					isClearable
					customInput={
						<Box position="relative">
							<Button borderRadius={isMobile ? 8 : 12} onPress={showDatePicker} gap={12} padding={isMobile ? 10 : 12} borderColor="$neutral300" borderWidth={1} justifyContent="flex-start">
								<Icon color={"$basicblack"} as={Calendar} />
								<Typography sx={{ fontSize: isMobile ? 14 : 16 }} weight={isMobile ? "normal" : "bold"}>
									{!startDate && !endDate && <Typography sx={{ fontSize: 15 }}>Select date</Typography>}
									{startDate && formatDispatchDate(startDate)} {endDate && <>- {formatDispatchDate(endDate)}</>}
								</Typography>
							</Button>
						</Box>
					}
				/>
			) : (
				<DatePicker
					portalId="root-portal"
					customInput={
						<Box backgroundColor={isLight ? "$basicwhite" : "transparent"} minWidth={minWidth} position="relative" zIndex={9}>
							<Button borderRadius={isMobile ? 8 : 12} onPress={showDatePicker} gap={12} padding={isMobile ? 10 : 12} borderWidth={1} justifyContent="flex-start">
								<Icon color={"$basicblack"} as={Calendar} />
								<Typography sx={{ fontSize: isMobile ? 14 : 16 }} weight={isMobile ? "normal" : "bold"}>
									{formatDispatchDate(date)}
								</Typography>
							</Button>
						</Box>
					}
					selected={date}
					onChange={handleConfirm}
					isClearable
				/>
			)}
		</Box>
	);
};

export default DateSelect;
